import { useContext } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import { Tendency } from '@cast/shared-types';
import { sumDataTransferCost, sumDataTransferTraffic } from '@cast/utils';

import { NetworkCostOverTimeContext } from '../../providers/NetworkCostOverTimeProvider';

export const useDataTransferSummary = () => {
  const { current, previous } = useContext(NetworkCostOverTimeContext);

  const aggregate = {
    cost: {
      egress: 0,
      ingress: 0,
    },
    traffic: {
      egress: 0,
      ingress: 0,
    },
  };

  const currentData = current.data.items;
  const currentAggregate = currentData.reduce(
    (acc, current) => {
      acc.cost.egress += sumDataTransferCost(current.egress);
      acc.cost.ingress += sumDataTransferCost(current.ingress);
      acc.traffic.egress += sumDataTransferTraffic(current.egress);
      acc.traffic.ingress += sumDataTransferTraffic(current.ingress);

      return acc;
    },
    { ...cloneDeep(aggregate) }
  );

  const previousData = previous.data.items;
  const previousAggregate = previousData.reduce(
    (acc, current) => {
      acc.cost.egress += sumDataTransferCost(current.egress);
      acc.cost.ingress += sumDataTransferCost(current.ingress);
      acc.traffic.egress += sumDataTransferTraffic(current.egress);
      acc.traffic.ingress += sumDataTransferTraffic(current.ingress);

      return acc;
    },
    { ...cloneDeep(aggregate) }
  );

  const currentAverageEgressCost =
    currentAggregate.cost.egress / currentData.length;
  const currentAverageIngressCost =
    currentAggregate.cost.ingress / currentData.length;

  const previousAverageEgressCost =
    previousAggregate.cost.egress / previousData.length;
  const previousAverageIngressCost =
    previousAggregate.cost.ingress / previousData.length;

  const currentAverageSum =
    currentAverageEgressCost + currentAverageIngressCost;
  const previousAverageSum =
    previousAverageEgressCost + previousAverageIngressCost;

  const totalDiff = previousAverageSum - currentAverageSum;
  const direction: Tendency =
    totalDiff === 0 ? 'stable' : totalDiff < 0 ? 'up' : 'down';

  return {
    ...currentAggregate,
    tendency: {
      display: previousAverageSum > 0,
      direction,
      diff: Math.abs(totalDiff),
    },
    averageDailyCost: {
      egress: currentAverageEgressCost,
      ingress: currentAverageIngressCost,
    },
    averageDailyTraffic: {
      egress: currentAggregate.traffic.egress / currentData.length,
      ingress: currentAggregate.traffic.ingress / currentData.length,
    },
  };
};
